import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Whether a keystroke was meant for a field somebody is typing into.
 *
 * `F` is a letter before it is a shortcut, and Escape in an open `<select>`
 * closes the list — neither may take the drawing surface with it.
 */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT";
}

/**
 * „Zeichenfläche maximieren" — one drawing surface over the whole window
 * (Entscheid Denis, 23.09.2026), shared by `/planung` and `/umriss`.
 *
 * **A fixed overlay, not the Fullscreen API**: the tools, the zoom bar and
 * the side panel have to stay in reach, and the browser's own fullscreen
 * costs a permission prompt and an Escape the page cannot order. What goes
 * big is decided by the screen's stylesheet on `data-maximized`; this hook
 * only holds the flag and the three things every surface needs with it:
 *
 * - **the page does not scroll underneath** — `body` overflow is locked
 *   while maximized and handed back exactly as it was;
 * - **`F` toggles** anywhere outside a text field, without Strg/⌘/Alt — a
 *   plain letter no other key on either screen uses (Strg+Shift+F is the
 *   browser's own on some systems, and a letter needs no second hand);
 * - **Escape ends it** — unless something else already answered that
 *   Escape (`defaultPrevented`), or the screen asks to order Escape itself
 *   (`exitOnEscape: false`): on `/planung` a running gesture, an open menu
 *   and a tool in hand all come first.
 *
 * Not in the undo stack and not on the wire — how big the board is drawn is
 * a way of looking, like `view`.
 */
export function useMaximize({ exitOnEscape = true }: { exitOnEscape?: boolean } = {}) {
  const [maximized, setMaximized] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const toggle = useCallback(() => setMaximized((was) => !was), []);
  /** End it and put the focus somewhere that still exists and still means
   * something: the button that started it. */
  const exit = useCallback(() => {
    setMaximized(false);
    buttonRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!maximized) return;
    const before = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = before;
    };
  }, [maximized]);

  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if (event.defaultPrevented) return;
      if (event.key === "Escape") {
        if (!maximized || !exitOnEscape || isTypingTarget(event.target)) return;
        exit();
        return;
      }
      if (
        (event.key === "f" || event.key === "F") &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey &&
        !isTypingTarget(event.target)
      ) {
        event.preventDefault();
        toggle();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [maximized, exitOnEscape, exit, toggle]);

  return { maximized, setMaximized, toggle, exit, buttonRef };
}
